/* import __COLOCATED_TEMPLATE__ from './reasons-interactive-bar.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { type OptimizeSuggestionItem } from 'embercom/services/optimize-suggestions-service';
import { inject as service } from '@ember/service';

export interface ReasonData {
  share: number;
  sentToTeammate: number;
  resolved: number;
  unresolved: number;
}

type ReasonKey = 'contentGaps' | 'dataGaps' | 'actionGaps';

interface Signature {
  Args: {
    suggestions: OptimizeSuggestionItem[];
    filterBy: (suggestionType: ReasonKey) => void;
  };
}

type Segment = {
  key: ReasonKey;
  label: string;
  colorClass: string;
  share: number;
};

export default class ReasonsInteractiveBar extends Component<Signature> {
  @tracked selected: ReasonKey | null = null;
  @service declare intl: IntlService;

  get contentSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === 'content-gap');
  }

  get dataSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === 'data-gap');
  }

  get actionSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === 'action-gap');
  }

  get segments(): Segment[] {
    let total = this.args.suggestions.length;
    return [
      {
        key: 'contentGaps',
        label: 'CONTENT GAPS',
        colorClass: 'bg-vis-mantis-10',
        share: total > 0 ? Math.round((this.contentSuggestions.length / total) * 100) : 0,
      },
      {
        key: 'dataGaps',
        label: 'DATA GAPS',
        colorClass: 'bg-vis-mantis-40',
        share: total > 0 ? Math.round((this.dataSuggestions.length / total) * 100) : 0,
      },
      {
        key: 'actionGaps',
        label: 'ACTION GAPS',
        colorClass: 'bg-vis-mantis-60',
        share: total > 0 ? Math.round((this.actionSuggestions.length / total) * 100) : 0,
      },
    ];
  }

  get gridTemplateColumns() {
    let spans = this.segments.map((segment) => (segment.share > 0 ? segment.share : 1));
    return spans.map((span) => `${span}fr`).join(' ');
  }

  get hasSelection() {
    return this.selected !== null;
  }

  @action
  isDimmed(key: ReasonKey): boolean {
    return this.selected !== null && this.selected !== key;
  }

  @action
  handleSelect(key: ReasonKey) {
    if (this.selected === key) {
      this.selected = null;
      this.args.filterBy?.(key);
      return;
    }

    this.selected = key;
    this.args.filterBy?.(key);
  }

  @action
  getReasonData(key: ReasonKey): ReasonData {
    let suggestionsByKey: Record<ReasonKey, OptimizeSuggestionItem[]> = {
      contentGaps: this.contentSuggestions,
      dataGaps: this.dataSuggestions,
      actionGaps: this.actionSuggestions,
    };

    let suggestions = suggestionsByKey[key];
    let total = this.args.suggestions.length;
    let share = total > 0 ? (suggestions.length / total) * 100 : 0;

    // TODO: Calculate actual values from suggestion data
    return {
      share,
      sentToTeammate: 0,
      resolved: 0,
      unresolved: 0,
    };
  }

  @action
  getReasonTitle(key: ReasonKey): string {
    let titles: Record<ReasonKey, string> = {
      contentGaps: 'CONTENT GAPS',
      dataGaps: 'DATA GAPS',
      actionGaps: 'ACTION GAPS',
    };
    return titles[key];
  }

  @action
  getReasonDescription(key: ReasonKey): string {
    let descriptions: Record<ReasonKey, string> = {
      contentGaps: this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.content-gaps',
      ),
      dataGaps: this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.data-gaps',
      ),
      actionGaps: this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.action-gaps',
      ),
    };
    return descriptions[key];
  }

  @action
  getReasonIcon(key: ReasonKey): InterfaceIconName {
    let icons: Record<ReasonKey, InterfaceIconName> = {
      contentGaps: 'knowledge',
      dataGaps: 'recipient-data',
      actionGaps: 'webhook',
    };
    return icons[key];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::ReasonsInteractiveBar': typeof ReasonsInteractiveBar;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/reasons-interactive-bar': typeof ReasonsInteractiveBar;
  }
}
